/**
 * ブラウザの位置情報の許可状態を見張る（Permissions API・タスク46.4 で投稿モーダルに実装 → タスク49 で共通化）。
 *
 * ブロック中は geolocation を呼んでも即座に拒否されるだけなので、先に状態を知って案内やボタンの可否を変える。
 * - watching（＝モーダルが開いている）間だけ購読し、閉じたら解除する
 * - 「許可」に変わったら onGranted を呼ぶ（設定を変えた瞬間に取り直す・押せるようにするため）
 * - navigator.geolocation が無ければ unsupported、Permissions API が無い・失敗したら unknown
 *   （unknown は従来どおり実際に取得を試みる扱い）
 */
import { useEffect, useRef, useState } from 'react';

export type GeolocationPermission =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'unknown'
  | 'unsupported';

export function useGeolocationPermission(
  watching: boolean,
  onGranted?: () => void,
): GeolocationPermission {
  const [permission, setPermission] = useState<GeolocationPermission>(
    () => (navigator.geolocation ? 'unknown' : 'unsupported'),
  );
  // 購読は開閉のたびに張り直すだけにしたいので、コールバックは ref で最新を呼ぶ
  const onGrantedRef = useRef(onGranted);
  onGrantedRef.current = onGranted;

  useEffect(() => {
    if (!watching) {
      return;
    }
    if (!navigator.geolocation) {
      setPermission('unsupported');
      return;
    }
    if (!navigator.permissions?.query) {
      setPermission('unknown');
      return;
    }
    let status: PermissionStatus | null = null;
    let cancelled = false;
    const onChange = () => {
      if (!status) {
        return;
      }
      setPermission(status.state);
      if (status.state === 'granted') {
        onGrantedRef.current?.();
      }
    };
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((s) => {
        if (cancelled) {
          return;
        }
        status = s;
        setPermission(s.state);
        s.addEventListener('change', onChange);
      })
      .catch(() => {
        if (!cancelled) {
          setPermission('unknown');
        }
      });
    return () => {
      cancelled = true;
      status?.removeEventListener('change', onChange);
    };
  }, [watching]);

  return permission;
}
